import React from 'react';

import { getPrivacyProfile } from '../config/privacy';
import {
  createEmptyInfoScreenData,
  getCachedInfoScreenData,
  getInfoScreenData
} from '../services/infoScreenService';
import { getJerusalemParts, millisecondsUntilNextMinute } from '../utils/date';

const RETRY_DELAYS_MS = [60_000, 120_000, 300_000];

function scheduleIdle(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout: 1500 });
    return () => window.cancelIdleCallback(id);
  }

  const id = window.setTimeout(callback, 0);
  return () => window.clearTimeout(id);
}

export default function useInfoScreenData({
  privacyProfile = getPrivacyProfile(false),
  enabled = true
} = {}) {
  const [data, setData] = React.useState(() => createEmptyInfoScreenData(Date.now(), privacyProfile));
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const mountedRef = React.useRef(false);
  const controllers = React.useMemo(() => new Set(), []);
  const inFlightRef = React.useRef(new Map());
  const requestSequenceRef = React.useRef(0);
  const lastAppliedRequestRef = React.useRef(0);
  const retryCountRef = React.useRef(0);
  const nextRetryAtRef = React.useRef(0);
  const dateKeyRef = React.useRef(getJerusalemParts().dateKey);

  const refresh = React.useCallback(
    ({ includeSupplemental = false, force = false } = {}) => {
      if (!enabled) {
        return Promise.resolve(null);
      }
      const phase = includeSupplemental ? 'supplemental' : 'critical';
      if (inFlightRef.current.has(phase)) {
        return inFlightRef.current.get(phase);
      }

      if (!force && !includeSupplemental && Date.now() < nextRetryAtRef.current) {
        return Promise.resolve(null);
      }

      const controller = new AbortController();
      const requestId = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestId;
      controllers.add(controller);
      setIsUpdating(true);

      const promise = getInfoScreenData({
        includeSupplemental,
        forceCritical: force,
        forceSupplemental: force,
        privacyProfile,
        signal: controller.signal
      })
        .then((nextData) => {
          if (!mountedRef.current || controller.signal.aborted) {
            return null;
          }

          if (requestId >= lastAppliedRequestRef.current) {
            lastAppliedRequestRef.current = requestId;
            React.startTransition(() => {
              setData(nextData);
              setIsInitialLoading(false);
            });
          }

          if (!includeSupplemental) {
            if (nextData.sync.failedResources > 0) {
              retryCountRef.current = Math.min(retryCountRef.current + 1, RETRY_DELAYS_MS.length);
              nextRetryAtRef.current =
                Date.now() + RETRY_DELAYS_MS[Math.max(0, retryCountRef.current - 1)];
            } else {
              retryCountRef.current = 0;
              nextRetryAtRef.current = 0;
            }
          }

          return nextData;
        })
        .catch((error) => {
          if (!controller.signal.aborted) {
            console.error('Failed to refresh info screen data', error);
          }
          return null;
        })
        .finally(() => {
          controllers.delete(controller);
          if (inFlightRef.current.get(phase) === promise) {
            inFlightRef.current.delete(phase);
          }
          if (mountedRef.current && inFlightRef.current.size === 0) {
            setIsUpdating(false);
            setIsInitialLoading(false);
          }
        });

      inFlightRef.current.set(phase, promise);
      return promise;
    },
    [controllers, enabled, privacyProfile]
  );

  React.useEffect(() => {
    const inFlightRequests = inFlightRef.current;

    if (!enabled) {
      setData(createEmptyInfoScreenData(Date.now(), privacyProfile));
      setIsInitialLoading(true);
      return undefined;
    }

    const cached = getCachedInfoScreenData(Date.now(), privacyProfile);
    setData(cached || createEmptyInfoScreenData(Date.now(), privacyProfile));
    setIsInitialLoading(!cached);
    mountedRef.current = true;
    refresh({ includeSupplemental: false });
    const cancelIdle = scheduleIdle(() => refresh({ includeSupplemental: true }));

    let minuteTimer;
    const scheduleMinuteRefresh = () => {
      minuteTimer = window.setTimeout(() => {
        scheduleMinuteRefresh();
        const currentDateKey = getJerusalemParts().dateKey;
        const dateChanged = currentDateKey !== dateKeyRef.current;
        dateKeyRef.current = currentDateKey;
        refresh({ includeSupplemental: false, force: dateChanged });
        if (dateChanged) {
          scheduleIdle(() => refresh({ includeSupplemental: true, force: true }));
        }
      }, millisecondsUntilNextMinute());
    };
    scheduleMinuteRefresh();

    const refreshAfterReconnect = () => {
      retryCountRef.current = 0;
      nextRetryAtRef.current = 0;
      refresh({ includeSupplemental: false, force: true });
      scheduleIdle(() => refresh({ includeSupplemental: true, force: true }));
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshAfterReconnect();
      }
    };

    window.addEventListener('online', refreshAfterReconnect);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      mountedRef.current = false;
      cancelIdle();
      window.clearTimeout(minuteTimer);
      window.removeEventListener('online', refreshAfterReconnect);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      controllers.forEach((controller) => controller.abort());
      controllers.clear();
      inFlightRequests.clear();
    };
  }, [controllers, enabled, privacyProfile, refresh]);

  const visibleData = React.useMemo(
    () =>
      data.privacy?.mode === privacyProfile.mode
        ? data
        : createEmptyInfoScreenData(Date.now(), privacyProfile),
    [data, privacyProfile]
  );

  return {
    data: visibleData,
    isInitialLoading,
    isUpdating,
    retry: () => refresh({ includeSupplemental: true, force: true })
  };
}
